"use client";

import { useMemo, useState } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/Button";
import {
    BEAUTY_CAMPAIGN_SAVINGS,
    formatCampaignDate,
} from "@/shared/campaign/beautyCampaign";
import type { OnboardingUiPhase, OnboardStep } from "@/shared/types/onboard.types";
import {
    DISPLAY_ONBOARD_STEPS,
    resolveStepVisualState,
} from "@/shared/utils/onboardStepsUi";

import styles from "./OnboardingProgress.module.scss";

const cx = cnBind.bind(styles);

type OnboardingProgressProps = {
    phase: OnboardingUiPhase;
    steps: OnboardStep[];
    adminUrl: string | null;
    contactEmail: string | null;
    telegramConfirmUrl: string | null;
    telegramBotUsername: string | null;
    onboardMessage: string | null;
    telegramLinked: boolean;
    errorMessage: string | null;
    campaignCode?: string | null;
    campaignFreeUntil?: string | null;
    onClose?: () => void;
};

const STEP_LABEL_KEYS: Record<string, string> = {
    TENANT_CREATED: "db",
    TRIAL_STARTED: "ssl",
    DIRECTOR_CREATED: "miniapp",
    EMAIL_SENT: "email",
};

export const OnboardingProgress = ({
    phase,
    steps,
    adminUrl,
    contactEmail,
    telegramConfirmUrl,
    telegramBotUsername,
    onboardMessage,
    telegramLinked,
    errorMessage,
    campaignCode,
    campaignFreeUntil,
    onClose,
}: OnboardingProgressProps) => {
    const { t } = useTranslation("common");
    const m = "home.onboardingProgress";
    const [copied, setCopied] = useState(false);

    const stepLabels = useMemo(
        () =>
            DISPLAY_ONBOARD_STEPS.map((step) => ({
                ...step,
                label: t(`${m}.steps.${STEP_LABEL_KEYS[step.code] || "db"}`),
            })),
        [t],
    );

    if (phase === "idle") return null;

    const showLoader = phase === "submitting" || phase === "provisioning";
    const showTelegramConfirm = phase === "telegram_confirm";
    const showSuccess = phase === "success";
    const showError = phase === "error";

    const botLabel = telegramBotUsername
        ? t(`${m}.openBotNamed`, { username: telegramBotUsername.replace(/^@/, "") })
        : t(`${m}.openBot`);

    const copyTelegramLink = () => {
        if (!telegramConfirmUrl) return;
        void navigator.clipboard.writeText(telegramConfirmUrl).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        });
    };

    const openTelegramBot = () => {
        if (!telegramConfirmUrl) return;
        window.open(telegramConfirmUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className={cx("overlay")} role="dialog" aria-modal="true" aria-labelledby="onboard-progress-title">
            <div
                className={cx(
                    "panel",
                    showSuccess && "panelSuccess",
                    showError && "panelError",
                    showTelegramConfirm && "panelTelegram",
                )}
            >
                {showLoader ? (
                    <>
                        <span className={cx("eyebrow")}>{t(`${m}.deployingEyebrow`)}</span>
                        <h2 id="onboard-progress-title" className={cx("title")}>
                            {phase === "submitting" ? t(`${m}.submitting`) : t(`${m}.launching`)}
                        </h2>
                        <p className={cx("subtitle")}>{t(`${m}.waitHint`)}</p>

                        <ol className={cx("steps")}>
                            {stepLabels.map((displayStep, index) => {
                                const state = resolveStepVisualState(displayStep, index, steps);
                                return (
                                    <li key={displayStep.code} className={cx("step", state)}>
                                        <span className={cx("stepIcon")} aria-hidden>
                                            {state === "done" ? "✓" : state === "active" ? "◌" : state === "failed" ? "!" : "○"}
                                        </span>
                                        <span className={cx("stepLabel")}>{displayStep.label}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    </>
                ) : null}

                {showTelegramConfirm ? (
                    <div className={cx("telegram")}>
                        <span className={cx("eyebrow")}>{t(`${m}.step1of2`)}</span>
                        <h2 id="onboard-progress-title" className={cx("title")}>
                            {t(`${m}.confirmTelegram`)}
                        </h2>
                        <p className={cx("subtitle")}>{onboardMessage || t(`${m}.needStart`)}</p>
                        {telegramLinked ? (
                            <p className={cx("hint", "hintSuccess")}>{t(`${m}.telegramLinked`)}</p>
                        ) : (
                            <p className={cx("hint")}>{t(`${m}.telegramHint`)}</p>
                        )}
                        {telegramConfirmUrl ? (
                            <div className={cx("actions")}>
                                <button type="button" className={cx("telegramButton")} onClick={openTelegramBot}>
                                    {botLabel}
                                </button>
                                <button type="button" className={cx("copyButton")} onClick={copyTelegramLink}>
                                    {copied ? t(`${m}.linkCopied`) : t(`${m}.copyLink`)}
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {showSuccess ? (
                    <div className={cx("success")}>
                        <span className={cx("successEmoji")} aria-hidden>
                            🎉
                        </span>
                        {campaignCode ? (
                            <>
                                <h2 className={cx("title")}>{t(`${m}.beautyTitle`)}</h2>
                                <p className={cx("subtitle")}>
                                    {campaignFreeUntil
                                        ? t(`${m}.beautySubtitleUntil`, {
                                              date: formatCampaignDate(campaignFreeUntil),
                                          })
                                        : t(`${m}.beautySubtitleMonths`)}
                                </p>
                                <p className={cx("subtitle")}>
                                    {t(`${m}.beautySavings`, { amount: BEAUTY_CAMPAIGN_SAVINGS })}
                                </p>
                            </>
                        ) : (
                            <>
                                <h2 className={cx("title")}>{t(`${m}.successTitle`)}</h2>
                                <p className={cx("subtitle")}>
                                    {contactEmail
                                        ? t(`${m}.passwordSentNamed`, { email: contactEmail })
                                        : t(`${m}.passwordSent`)}{" "}
                                    {t(`${m}.panelHint`)}
                                </p>
                            </>
                        )}
                        {adminUrl ? (
                            <Button
                                label={campaignCode ? t(`${m}.setupBeauty`) : t(`${m}.enterCrm`)}
                                variant="solid"
                                isFullWidth
                                iconTrue={false}
                                onClick={() => window.open(adminUrl, "_blank", "noopener,noreferrer")}
                            />
                        ) : null}
                        {onClose ? (
                            <button type="button" className={cx("closeLink")} onClick={onClose}>
                                {t(`${m}.close`)}
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {showError ? (
                    <div className={cx("error")}>
                        <h2 className={cx("title")}>{t(`${m}.errorTitle`)}</h2>
                        <p className={cx("subtitle")}>{errorMessage || t(`${m}.errorFallback`)}</p>
                        {onClose ? (
                            <Button
                                label={t(`${m}.close`)}
                                variant="white"
                                isFullWidth
                                iconTrue={false}
                                onClick={onClose}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
